import PropTypes from 'prop-types';

import { Company, Role, User } from '@adsk/alloy-provider';

import { encodeMentionsName } from './utils/parseMentionsText';

export const MENTION_TYPES = {
  USER: 'user',
  COMPANY: 'company',
  ROLE: 'role',
} as const;

type MentionTypes = typeof MENTION_TYPES;
export type MentionType = MentionTypes[keyof MentionTypes];
export type MentionSupportedTypes = User | Company | Role;

export interface MentionData {
  type: MentionType;
  name: string;
  id: string;
}

// MENTION_TAG_REGEX - regex pattern to match a mention tag with its props.
// For Example: @{"type":"TYPE","id":"ID","name":"Name"}
// Output: match template and parameters for a mention tag
// [
//   "@{\"type\":\"TYPE\",\"id\":\"ID\",\"name\":\"Name\"}",
//   "TYPE",
//   "ID",
//   "Name"
// ]
export const MENTION_TAG_REGEX =
  /@\{\s?"type"\s?:\s?"(.+?)"\s?,\s?"id"\s?:\s?"(.+?)"\s?,\s?"name"\s?:\s?"(.+?)"\s?\}/g;
export const SLICE_START = 0;
export const USERS_SIZE = 6;
export const COMPANIES_SIZE = 2;
export const ROLES_SIZE = 2;
export const MENTION_CONFIRMATION_USERS_LIMIT = 5;
export const MENTION_CONFIRMATION_MENTIONS_LIMIT = 2;

export const MentionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(MENTION_TYPES)).isRequired,
}).isRequired;

export const MENTION_ANALYTICS = {
  ERROR: 'mention.error',
  CREATE_START: 'mention.create.start',
  CREATE_SELECT: 'mention.create.select',
  CREATE_REMOVE: 'mention.create.remove',
  CREATE_END: 'mention.create.noresult',
  VIEW_HOVER: 'mention.view.hover',
  VIEW_CLICK: 'mention.view.click',
};

export function defaultGetMentionName(mention: MentionData) {
  return encodeMentionsName(mention.name);
}

export function defaultTypeToSize(type: MentionType): number {
  switch (type) {
    case MENTION_TYPES.USER:
      return USERS_SIZE;
    case MENTION_TYPES.COMPANY:
      return COMPANIES_SIZE;
    case MENTION_TYPES.ROLE:
      return ROLES_SIZE;
    default:
      return USERS_SIZE;
  }
}

export const getItemIdByMentionType = (
  type: MentionType,
  item: MentionSupportedTypes,
) => {
  if (type === MENTION_TYPES.USER && 'autodeskId' in item) {
    return item.autodeskId;
  } else if (type === MENTION_TYPES.ROLE && 'memberGroupId' in item) {
    return item.memberGroupId;
  } else if (type === MENTION_TYPES.COMPANY && 'memberGroupId' in item) {
    return item.memberGroupId;
  }

  return item.id;
};

export function defaultToMentionData(
  data: MentionSupportedTypes[],
  type: MentionType,
): MentionData[] {
  return data.map((item) => ({
    type,
    id: getItemIdByMentionType(type, item),
    name: item.name,
  }));
}

export function defaultFilter<T extends MentionSupportedTypes>(data: T[]): T[] {
  return data
    ?.filter((item) => item.name)
    ?.filter((item) => item.status === 'active');
}
// coped from: https://github.com/draft-js-plugins/draft-js-plugins/blob/master/packages/mention/src/defaultRegExp.ts
// for adding Spacial Characters support
// TODO: remove this once we have Spacial Characters support in the draft-js-plugins repo [WPX-651]
export const defaultRegExp =
  '[' +
  '\\w-' +
  // Latin-1 Supplement (letters only) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin-1_Supplement
  '\u00C0-\u00D6' +
  '\u00D8-\u00F6' +
  '\u00F8-\u00FF' +
  // Latin Extended-A (without deprecated character) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin_Extended-A
  '\u0100-\u0148' +
  '\u014A-\u017F' +
  // Cyrillic symbols: \u0410-\u044F - https://en.wikipedia.org/wiki/Cyrillic_script_in_Unicode
  '\u0410-\u044F' +
  // Symbols that are sometimes used in Japanese names: \u3005-\u3006
  '\u3005-\u3006' +
  // hiragana (japanese): \u3040-\u309F - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  '\u3040-\u309F' +
  // katakana (japanese): \u30A0-\u30FF - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  '\u30A0-\u30FF' +
  // For an advanced explaination about Hangul see https://github.com/draft-js-plugins/draft-js-plugins/pull/480#issuecomment-254055437
  // Hangul Jamo (korean): \u3130-\u318F - https://en.wikipedia.org/wiki/Korean_language_and_computers#Hangul_in_Unicode
  // Hangul Syllables (korean): \uAC00-\uD7A3 - https://en.wikipedia.org/wiki/Korean_language_and_computers#Hangul_in_Unicode
  '\u3130-\u318F' +
  '\uAC00-\uD7A3' +
  // common chinese symbols: \u4e00-\u9eff - http://stackoverflow.com/a/1366113/837709
  // extended to \u9fa5 https://github.com/draft-js-plugins/draft-js-plugins/issues/1888
  '\u4e00-\u9fa5' +
  // Arabic https://en.wikipedia.org/wiki/Arabic_(Unicode_block)
  '\u0600-\u06ff' +
  // Vietnamese http://vietunicode.sourceforge.net/charset/
  '\u00C0-\u1EF9' +
  // !	EXCLAMATION MARK (U+0021)	21
  // "	QUOTATION MARK (U+0022)	22
  // #	NUMBER SIGN (U+0023)	23
  // $	DOLLAR SIGN (U+0024)	24
  // %	PERCENT SIGN (U+0025)	25
  // &	AMPERSAND (U+0026)	26
  // '	APOSTROPHE (U+0027)	27
  // (	LEFT PARENTHESIS (U+0028)	28
  // )	RIGHT PARENTHESIS (U+0029)	29
  // *	ASTERISK (U+002A)	2a
  // +	PLUS SIGN (U+002B)	2b
  // ,	COMMA (U+002C)	2c
  // -	HYPHEN-MINUS (U+002D)	2d
  // .	FULL STOP (U+002E)	2e
  // /	SOLIDUS (U+002F)	2f
  // Spacial Characters https://www.fileformat.info/info/charset/UTF-8/list.htm
  '\u0021-\u002f' +
  // :	COLON (U+003A)	3a
  // ;	SEMICOLON (U+003B)	3b
  // <	LESS-THAN SIGN (U+003C)	3c
  // =	EQUALS SIGN (U+003D)	3d
  // >	GREATER-THAN SIGN (U+003E)	3e
  // ?	QUESTION MARK (U+003F)	3f
  // @	COMMERCIAL AT (U+0040)	40
  // Spacial Characters https://www.fileformat.info/info/charset/UTF-8/list.htm
  '\u003a-\u0040' +
  ']';
